import { Box, Grid, Alert, Typography, Snackbar } from "@mui/material";
import React, { useState } from "react";
import { useGetMyAbsenceBalanceQuery } from "../../../api/absenceBalancesApi";
import { useGetMyAbsenceRequestsQuery, useCancelAbsenceRequestMutation } from "../../../api/absenceRequestsApi";
import { BalanceSummary } from "../../../components/absence";
import { LoadingSpinner } from "../../../components/common/LoadingSpinner";
import { AddButton } from "../../../components/common/AddButton";
import { ConfirmationDialog } from "../../../components/common/ConfirmationDialog/ConfirmationDialog";
import MyAbsenceRequestsList from "./MyAbsenceRequestsList";
import { CreateAbsenceRequestDialog } from "./CreateAbsenceRequestDialog";
import { ViewAbsenceRequestDialog } from "./ViewAbsenceRequestDialog";
import { AbsenceRequestResponseDto } from "../../../types/absence";
import { canCancelRequest } from "../../../utils/absenceUtils";
import { useInfiniteDataTableItems } from "../../../hooks/useInfiniteDataTableItems";
import { DEFAULT_DATA_TABLE_PAGE_SIZE } from "../../../constants/dataTable";

const EmployeeAbsencesTab: React.FC = () => {
  const [page, setPage] = useState(0);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState<AbsenceRequestResponseDto | null>(null);
  const [requestToView, setRequestToView] = useState<AbsenceRequestResponseDto | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data: balance, isLoading: isBalanceLoading, error: balanceError } = useGetMyAbsenceBalanceQuery();
  const {
    data: requestsData,
    isLoading: isRequestsLoading,
    isFetching: isRequestsFetching,
    refetch: refetchRequests,
  } = useGetMyAbsenceRequestsQuery({
    pageNumber: page + 1,
    pageSize: DEFAULT_DATA_TABLE_PAGE_SIZE,
  });
  const { accumulatedItems: requests, resetAccumulatedItems } =
    useInfiniteDataTableItems<AbsenceRequestResponseDto>(requestsData?.items, page);

  const [cancelRequest, { isLoading: isCanceling }] = useCancelAbsenceRequestMutation();

  const resetRequestsTable = () => {
    resetAccumulatedItems();
    setPage(0);
  };

  const handleRequestAbsence = () => {
    setIsCreateDialogOpen(true);
  };

  const handleCreateSuccess = () => {
    resetRequestsTable();
    refetchRequests();
  };

  const handleViewDetails = (request: AbsenceRequestResponseDto) => {
    setRequestToView(request);
  };

  const handleCancelRequest = (request: AbsenceRequestResponseDto) => {
    if (!canCancelRequest(request.status, request.startDate)) {
      return;
    }
    setRequestToCancel(request);
  };

  const handleConfirmCancel = async () => {
    if (!requestToCancel) return;

    try {
      await cancelRequest(requestToCancel.id).unwrap();
      setSuccessMessage("Absence request cancelled successfully");
      setRequestToCancel(null);
      resetRequestsTable();
      refetchRequests();
    } catch (error) {
      console.error("Failed to cancel request:", error);
      setRequestToCancel(null);
    }
  };

  const handleCloseConfirmDialog = () => {
    setRequestToCancel(null);
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  const handleLoadMore = () => {
    if (requestsData?.hasNextPage && !isRequestsFetching) {
      setPage((currentPage) => currentPage + 1);
    }
  };

  if (isBalanceLoading && isRequestsLoading) {
    return <LoadingSpinner />;
  }

  if (balanceError) {
    return (
      <Alert severity="error">
        Failed to load absence balance. Please try again later.
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          My Absences
        </Typography>
        <AddButton onClick={handleRequestAbsence}>Request Absence</AddButton>
      </Box>

      {balance ? (
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={6} lg={4}>
            <BalanceSummary
              vacationDaysAvailable={balance.vacationDaysAvailable}
              vacationDaysUsed={balance.vacationDaysUsed}
              vacationDaysCarryOver={balance.vacationDaysCarryOver}
              sickDaysAvailable={balance.sickDaysAvailable}
              sickDaysUsed={balance.sickDaysUsed}
              vacationEligibilityDate={balance.vacationEligibilityDate}
              accruedVacationDays={balance.accruedVacationDays}
            />
          </Grid>
        </Grid>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          No absence balance information available. Please contact your administrator.
        </Alert>
      )}

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          My Absence Requests
        </Typography>
        <MyAbsenceRequestsList
          requests={requests}
          isLoading={isRequestsLoading && requests.length === 0}
          onCancelClick={handleCancelRequest}
          onViewDetailsClick={handleViewDetails}
          totalCount={requestsData?.totalCount}
          hasNextPage={requestsData?.hasNextPage}
          isLoadingMore={isRequestsFetching && requests.length > 0}
          onLoadMore={handleLoadMore}
        />
      </Box>

      {/* Create Absence Request Dialog */}
      <CreateAbsenceRequestDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* View Request Details Dialog */}
      <ViewAbsenceRequestDialog
        open={!!requestToView}
        request={requestToView}
        onClose={() => setRequestToView(null)}
      />

      {/* Cancel Confirmation Dialog */}
      <ConfirmationDialog
        open={!!requestToCancel}
        title="Cancel Absence Request"
        message={`Are you sure you want to cancel this ${
          requestToCancel?.absenceCategoryName || "absence"
        } request from ${
          requestToCancel?.startDate
            ? new Date(requestToCancel.startDate).toLocaleDateString()
            : ""
        } to ${
          requestToCancel?.endDate
            ? new Date(requestToCancel.endDate).toLocaleDateString()
            : ""
        }?`}
        confirmLabel="Cancel Request"
        cancelLabel="Keep Request"
        confirmColor="warning"
        onConfirm={handleConfirmCancel}
        onClose={handleCloseConfirmDialog}
        isLoading={isCanceling}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={successMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default EmployeeAbsencesTab;






